// Pure text and tone for the Providers tab. Every string is the approved copy (n12 MOCK-README).
import type {
  UsageAccount,
  UsageComputer,
  UsageSpend,
  UsageWindow,
  SwitchFailure,
} from "./provider-usage-types.js";
import type { SwitchState } from "./provider-usage-state.svelte.js";

// copy: approved (Fab). One home so the strings are not retyped in components.
export const COPY = {
  weeklyNone: "Weekly: no reading yet",
  windowReset: "Window reset, waiting for a new reading",
  codexNoData: "No usage data yet",
  codexNoDataHint: "Codex reports usage after its first session on this computer",
  codexNote:
    "Codex accounts can't be switched from here. Switch with codex-account on the computer.",
  notSwitchable: "Not switchable here",
  otherProviders: "Other providers",
  otherNote: "Counted from this computer's turns",
  adminView: "You are viewing as workspace admin.", // copy: approved (Fab, mock v4)
  today: "Today",
  last7Days: "Last 7 days",
  lastTurn: "Last turn",
  noTurns: "No turns yet",
  apiKey: "API key",
} as const;
// copy: approved (Fab). A provider on this computer with no turns yet.
export const NO_USAGE_YET = "No usage yet";

export interface FormatCtx {
  now: number;
  /** IANA zone; undefined = the viewer's own. */
  timeZone?: string;
}
export type BarTone = "accent" | "attention" | "failed";
export type RefreshOutcome = "updated" | "no_answer" | "offline" | "refused";
export interface RefreshAllKpi {
  computerCount: "1" | "2-3" | "4-10" | "11+";
  outcome: "all_answered" | "some_no_answer" | "some_offline" | "some_refused";
}

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

export function barTone(pct: number): BarTone {
  if (pct >= 100) return "failed";
  return pct >= 80 ? "attention" : "accent";
}

/** A window whose reset time has passed: its reading belongs to the window that ended. */
export function hasReset(w: UsageWindow, now: number): boolean {
  return w.resetsAt !== null && w.resetsAt <= now;
}

/** The window as of `now`: once it has reset, its old percentage and status are no longer known. */
export function liveWindow(w: UsageWindow, now: number): UsageWindow {
  return hasReset(w, now) ? { ...w, usedPct: null, status: "unknown" } : w; // null: the wire shape's "no reading"
}

/** With `now`, a window past its reset is no longer at its limit; without it, the reading as observed. */
export function isLimitReached(w: UsageWindow, now?: number): boolean {
  if (now !== undefined && hasReset(w, now)) return false;
  return w.status === "rejected" || (w.usedPct ?? 0) >= 100;
}

/** One order for a computer's accounts, whatever order they arrived in: provider, then account name, key. Active is marked in place and never moves. */
export function sortAccounts(accounts: readonly UsageAccount[]): UsageAccount[] {
  const text = (a: string, b: string) => {
    if (a === b) return 0;
    return a < b ? -1 : 1;
  };
  return accounts.toSorted(
    (a, b) =>
      text(a.provider, b.provider) ||
      text(a.switcherName ?? a.label ?? "\uffff", b.switcherName ?? b.label ?? "\uffff") ||
      text(a.accountKey, b.accountKey),
  );
}

export function usedText(w: UsageWindow): string {
  if (w.usedPct === undefined || w.usedPct === null) return "—";
  return `${Math.round(w.usedPct)}% used`;
}

function clock(ts: number, timeZone: string | undefined, zone = false): string {
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone,
    ...(zone ? { timeZoneName: "short" as const } : {}),
  }).format(ts);
}

function part(ts: number, timeZone: string | undefined, opts: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat("en-US", { timeZone, ...opts }).format(ts);
}

function relative(ms: number): string {
  const total = Math.max(1, Math.round(ms / MIN));
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h === 0 ? `${m} min` : `${h} h ${String(m).padStart(2, "0")} min`;
}

function futureReset(w: UsageWindow, now: number): number | undefined {
  return w.resetsAt !== null && w.resetsAt > now ? w.resetsAt : undefined;
}

function weeklyList(at: number, tz: string | undefined): string {
  return `${part(at, tz, { weekday: "short" })} ${clock(at, tz)}`;
}

export function resetsList(w: UsageWindow, ctx: FormatCtx): string {
  const at = futureReset(w, ctx.now);
  if (at === undefined) return "";
  const when =
    w.kind === "five_hour" ? `in ${relative(at - ctx.now)}` : weeklyList(at, ctx.timeZone);
  if (isLimitReached(w, ctx.now)) return `Limit reached · resets ${when}`;
  if (w.kind === "five_hour") return `Resets ${when} (${clock(at, ctx.timeZone)})`;
  return `Resets ${when}`;
}

/** The offline line: "Offline. Last known: weekly 90% used, resets Mon 9:00 AM." */
export function offlineLastKnown(w: UsageWindow | undefined, ctx: FormatCtx): string {
  if (!w || w.usedPct === null || w.usedPct === undefined) return "";
  const used = isLimitReached(w, ctx.now) ? 100 : Math.round(w.usedPct);
  const at = futureReset(w, ctx.now);
  const reset = at === undefined ? "" : `, resets ${weeklyList(at, ctx.timeZone)}`;
  return `Offline. Last known: weekly ${used}% used${reset}.`;
}

export function resetsDetail(w: UsageWindow, ctx: FormatCtx): string {
  const at = futureReset(w, ctx.now);
  if (at === undefined) return "";
  const time = clock(at, ctx.timeZone, true);
  if (w.kind === "five_hour") return `Resets in ${relative(at - ctx.now)} · ${time}`;
  const day = part(at, ctx.timeZone, { weekday: "short", month: "short", day: "numeric" });
  return `Resets ${day.replace(",", "")} · ${time}`;
}

/** The "Weekly" cell of the switcher table: "58% · resets Sun". */
export function weeklyShort(window: UsageWindow, ctx: FormatCtx): string {
  const w = liveWindow(window, ctx.now);
  const pct = w.usedPct === null ? "—" : `${Math.round(w.usedPct)}%`;
  const at = futureReset(w, ctx.now);
  if (at === undefined) return pct;
  return `${pct} · resets ${part(at, ctx.timeZone, { weekday: "short" })}`;
}

/** Two days or more shows the date ("Oct 27"): the approved copy has no "N days ago". */
export function ageText(at: number, now: number, timeZone?: string): string {
  const diff = Math.max(0, now - at);
  if (diff < MIN) return "just now";
  if (diff < HOUR) return `${Math.floor(diff / MIN)} min ago`;
  if (diff < DAY) return `${Math.floor(diff / HOUR)} h ago`;
  if (diff < 2 * DAY) return "yesterday";
  return part(at, timeZone, { month: "short", day: "numeric" });
}

export function updatedText(at: number, now: number): string {
  return `Updated ${ageText(at, now)}`;
}

export function offlineText(dataAsOf: number | null | undefined, timeZone?: string): string {
  if (dataAsOf === null || dataAsOf === undefined) return "Offline";
  return `Offline · data from ${clock(dataAsOf, timeZone)}`;
}

export function noAnswerText(dataAsOf: number | null | undefined, timeZone?: string): string {
  if (dataAsOf === null || dataAsOf === undefined) return "Didn't answer";
  return `Didn't answer: showing data from ${clock(dataAsOf, timeZone)}`;
}

export function providerName(provider: string): string {
  return provider.charAt(0).toUpperCase() + provider.slice(1);
}

/** Wire plan id ("max_5x") to its display name ("Max 5x"); an unknown id is only title-cased, a display name is kept. */
export function planLabel(plan: string | null | undefined): string | undefined {
  // A provider display name ("Ultra", "Max 5x") is shown as written; only lowercase ids are title-cased.
  if (plan && /[A-Z ]/.test(plan)) return plan.trim() || undefined;
  const words = (plan ?? "").split(/[_\-\s]+/).filter((w) => w !== "");
  if (words.length === 0) return undefined;
  return words
    .map((w) =>
      /^\d+x$/i.test(w) ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
    )
    .join(" ");
}

export function accountTitle(a: UsageAccount): string {
  const label = a.label ?? (a.authKind === "api_key" ? "API key" : a.switcherName);
  return label ? `${providerName(a.provider)} · ${label}` : providerName(a.provider);
}

export function formatUsd(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v);
}

/** Where an API key's spend was read from (SPEC §4c labels). */
export function spendScopeText(scope: UsageSpend["scope"]): string {
  return scope === "all_sessions" ? "All sessions on this computer" : "Agent sessions only";
}

/** "412 K tokens", "1.2 M tokens"; "" under a thousand. */
export function formatTokens(n: number): string {
  if (n < 1_000) return "";
  if (n < 999_500) return `${Math.round(n / 1_000)} K tokens`;
  const m = n / 1_000_000;
  return `${m >= 100 ? Math.round(m) : Math.round(m * 10) / 10} M tokens`;
}

/** "1.2 M tokens · 52 sessions" or "· 1 session". */
export function spendWeekLine(spend: UsageSpend): string {
  const sessions = spend.sessions === 1 ? "1 session" : `${spend.sessions} sessions`;
  return [formatTokens(spend.weekTokens), sessions].filter((s) => s !== "").join(" · ");
}

function ownerPart(c: UsageComputer): string | undefined {
  if (c.mine) return c.worker ? "owned by you" : undefined;
  return c.ownerName ? `owned by ${c.ownerName}` : undefined;
}

function lastSeenPart(c: UsageComputer, now: number): string | undefined {
  if (c.online || c.lastSeenAt === null) return undefined;
  return `last seen ${ageText(c.lastSeenAt, now)}`;
}

/** A computer nothing has been read from yet: "0 accounts" there would be a guess. */
export function isUnread(c: UsageComputer): boolean {
  return c.dataAsOf === null && c.accounts.length === 0;
}

export function computerMeta(c: UsageComputer, now: number): string {
  const n = c.accounts.length;
  const count = n === 1 ? "1 account" : `${n} accounts`;
  const switchers = [
    ...new Set(c.accounts.flatMap((a) => (a.switcher && a.switcherName ? [a.switcher] : []))),
  ];
  return [
    ownerPart(c),
    isUnread(c) ? undefined : count,
    (c.mine || c.worker) && switchers.length > 0 ? `switcher: ${switchers.join(", ")}` : undefined,
    lastSeenPart(c, now),
  ]
    .filter((s): s is string => s !== undefined)
    .join(" · ");
}

function countBucket(n: number): RefreshAllKpi["computerCount"] {
  if (n === 1) return "1";
  if (n <= 3) return "2-3";
  return n <= 10 ? "4-10" : "11+";
}

function worstOutcome(results: RefreshOutcome[]): RefreshAllKpi["outcome"] {
  if (results.includes("offline")) return "some_offline";
  if (results.includes("refused")) return "some_refused";
  return results.includes("no_answer") ? "some_no_answer" : "all_answered";
}

export function refreshAllKpi(results: RefreshOutcome[]): RefreshAllKpi | undefined {
  if (results.length === 0) return undefined;
  const worst = worstOutcome(results);
  return { computerCount: countBucket(results.length), outcome: worst };
}

export function switchAccountName(a: UsageAccount): string {
  return a.label ?? a.switcherName ?? providerName(a.provider);
}

/** A Claude result line ("Now using X", "still using X") holds only while the rows still say so. */
export function resultLineHolds(s: SwitchState, c: UsageComputer): boolean {
  if (s.kind !== "done" || s.provider === "codex") return true;
  const active = c.accounts.filter((a) => a.provider === s.provider && a.active);
  if (active.length === 0) return true; // no row claims Active (a re-read that failed): nothing contradicts it
  // "still using A" holds only while A is the Active one; "Now using X" only while X is.
  const name = s.stillUsing ?? s.name;
  const isNamed = (a: UsageAccount) => switchAccountName(a) === name || a.label === name || a.switcherName === name; // prettier-ignore
  return active.some((a) => isNamed(a) || (s.stillUsing === undefined && a.accountKey === s.accountKey)); // prettier-ignore
}

/** Owner: any computer. A viewer who is not the owner only ever sees workspace computers (the relay hides personal ones). Codex is never switched from here. */
export function canSwitch(c: UsageComputer, a: UsageAccount): boolean {
  if (!(c.mine || c.worker) || a.active || a.authKind === "api_key") return false;
  if (a.switcherName?.startsWith("-")) return false;
  return a.switcher === "claude-swap";
}

export function switchConfirmBody(n: number, computer: string, account: string): string {
  const one = n === 1;
  const running = `${n} ${one ? "agent is" : "agents are"} running on ${computer}.`;
  const uses = one
    ? `It uses ${account} from its next message.`
    : `They use ${account} from their next message.`;
  return `Claude: ${running} ${uses} Work in progress is not stopped.`;
}

const SWITCH_REASON: Record<SwitchFailure, (switcher: string) => string> = {
  not_installed: (s) => `${s} is not installed on this computer`,
  timed_out: (s) => `${s} did not answer in 30 seconds`,
  could_not_switch: (s) => `${s} could not switch`,
  binary_writable: (s) => `Other users can change ${s}, so Cyborg did not run it`,
  binary_in_workspace: (s) => `${s} is inside an agent's working folder, so Cyborg did not run it`,
};

export interface SwitchFailedFacts {
  switcher: string;
  reason: SwitchFailure;
  provider: string;
  active: string | null;
  answered: boolean;
  unchanged: boolean;
}

export function switchFailedBody(f: SwitchFailedFacts): string {
  const reason = `${SWITCH_REASON[f.reason](f.switcher)}.`;
  // It was stopped mid-way, so nothing about the account is known until the next read.
  if (f.reason === "timed_out")
    return `${reason} Cyborg stopped it. Refresh to check which account is active.`;
  if (!f.answered) return reason;
  return f.active ? `${reason} ${f.active} is still active.` : reason;
}

export interface UsageCells {
  five: UsageWindow | undefined;
  weekly: UsageWindow | undefined;
  /** Named limits (Fable); the per-model weeklies stay hidden. */
  named: UsageWindow[];
}

export function usageCells(a: UsageAccount): UsageCells {
  return {
    five: a.windows.find((w) => w.kind === "five_hour"),
    weekly: a.windows.find((w) => w.kind === "seven_day"),
    named: a.windows.filter((w) => w.kind === "other" && w.label !== undefined),
  };
}

/** "as of 3h ago" on an offline computer's greyed bars. */
export function asOfText(at: number | null | undefined, now: number): string {
  return at === null || at === undefined ? "" : `as of ${ageText(at, now)}`;
}

/** Claude and Codex have a limit reader; every other provider is counted from its turns only. */
const LIMIT_READERS: readonly string[] = ["claude", "codex"];

/**
 * A row of the per-turn ledger ("Other providers"): a provider with no limit reader. An API-key row from an
 * older daemon (no todayTokens on its spend) keeps the full spend row under its own provider heading.
 */
function isLedgerRow(a: UsageAccount): boolean {
  if (LIMIT_READERS.includes(a.provider)) return false;
  return !(a.authKind === "api_key" && a.spend !== null && a.spend.todayTokens === undefined);
}

/** Accounts shown with their own heading, split by provider, Claude first. */
export function providerGroups(
  accounts: readonly UsageAccount[],
): Array<{ provider: string; accounts: UsageAccount[] }> {
  const rank = (p: string) =>
    LIMIT_READERS.includes(p) ? LIMIT_READERS.indexOf(p) : LIMIT_READERS.length;
  const groups = new Map<string, UsageAccount[]>();
  for (const a of accounts)
    if (!isLedgerRow(a)) groups.set(a.provider, [...(groups.get(a.provider) ?? []), a]);
  return [...groups.entries()]
    .toSorted(([a], [b]) => rank(a) - rank(b) || (a < b ? -1 : 1))
    .map(([provider, list]) => ({ provider, accounts: list }));
}

/** The "Other providers" rows: the per-turn ledger of providers with no limit reader. */
export function otherAccounts(accounts: readonly UsageAccount[]): UsageAccount[] {
  return accounts.filter(isLedgerRow);
}

/** "12 K tokens", or "840 tokens" under a thousand. */
function tokensText(n: number): string {
  return formatTokens(n) || `${n} tokens`;
}

/** "412 K tokens · $0.31": the dollars only when the provider reported a cost. */
export function ledgerFigure(tokens: number, costUsd: number | null | undefined): string {
  const t = tokensText(tokens);
  return typeof costUsd === "number" ? `${t} · ${formatUsd(costUsd)}` : t;
}

/** A provider with no turns counted: no spend row, or every total zero. */
export function hasNoTurns(spend: UsageSpend | null): boolean {
  return (
    !spend || (spend.weekTokens === 0 && spend.monthTokens === 0 && (spend.todayTokens ?? 0) === 0)
  );
}

/**
 * The ledger stamps the daemon's local midnight, never an hour: "Today" is the daemon's todayTokens
 * within 25 h of the stamp (an offline computer's stale figure never says it), else whole days.
 */
export function lastTurnText(at: number, now: number, todayTokens?: number): string {
  if (((todayTokens ?? 0) > 0 && now - at < DAY + HOUR) || now - at < DAY) return COPY.today;
  if (now - at < 2 * DAY) return "yesterday";
  // +12 h names the daemon's day only for a viewer within 12 h of its zone
  return part(at + DAY / 2, undefined, { month: "short", day: "numeric" });
}

/** Providers the machine runs that have no usage row and no local usage source. */
export function noSourceProviders(
  providers: readonly { id: string; label: string }[] | undefined,
  accounts: readonly UsageAccount[],
): Array<{ id: string; label: string }> {
  const shown = new Set(accounts.map((a) => a.provider));
  return (providers ?? []).filter((p) => !shown.has(p.id) && p.id !== "claude" && p.id !== "codex");
}

// copy: approved (Fab). The window an agent waits on, as the detail page's sentence names it.
export function limitSentence(reached: UsageWindow | undefined): string {
  if (reached?.kind === "five_hour") return "5-hour window";
  return reached?.label ? `${reached.label} limit` : "weekly limit";
}
